import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { categoryColorMap } from '@/mocks/categories';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const catColor = article.category === 'pingtai-tuijian' ? 'accent' : article.category === 'liaotian-huashu' ? 'secondary' : 'primary';
  const catInfo = categoryColorMap[catColor];

  if (variant === 'compact') {
    const dotColor = article.category === 'pingtai-tuijian' ? 'bg-accent-500' : article.category === 'liaotian-huashu' ? 'bg-secondary-500' : 'bg-primary-500';

    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${dotColor}`}></div>
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-4 md:p-5">
          <span className={`inline-block text-xs px-2 py-1 rounded-full ${catInfo.bg} ${catInfo.text} mb-3`}>
            {article.categoryName}
          </span>
          <h3 className="text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all"
    >
      <div className="w-full sm:w-48 h-32 shrink-0 rounded-lg overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <span className={`inline-block text-xs px-2 py-1 rounded-full ${catInfo.bg} ${catInfo.text} mb-2 w-fit`}>
          {article.categoryName}
        </span>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-1.5">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-2">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {article.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}